/**
 * StoragePathService
 *
 * Utilities for extracting and validating storage paths from various formats:
 * - Raw storage paths: "user-123/photos/image.jpg"
 * - Paths with bucket prefix: "identity-documents/user-123/photos/image.jpg"
 * - Signed URLs: "https://xxx.supabase.co/storage/v1/object/sign/bucket/path?token=..."
 * - Public URLs: "https://xxx.supabase.co/storage/v1/object/public/bucket/path"
 */

import { Result, ServiceError } from '@kit/shared/result';

import { StorageBuckets, type StorageBucket } from './storage-url.service';

/**
 * Service for handling storage path extraction and validation
 */
export class StoragePathService {
  /**
   * Extract storage path from any URL or path format
   * Works with ANY bucket, not hardcoded to a specific one
   *
   * @param fullPath - Can be a raw path, bucket-prefixed path, signed URL, or public URL
   * @param expectedBucket - Optional bucket to strip from the path
   * @returns Result containing the extracted path or an error
   *
   * @example
   * ```typescript
   * // From signed URL
   * StoragePathService.extractPath(
   *   'https://xxx.supabase.co/storage/v1/object/sign/identity-documents/user-123/doc.pdf?token=...',
   *   StorageBuckets.IDENTITY_DOCUMENTS
   * )
   * // Result: 'user-123/doc.pdf'
   *
   * // From bucket-prefixed path
   * StoragePathService.extractPath('identity-documents/user-123/doc.pdf')
   * // Result: 'user-123/doc.pdf'
   *
   * // From raw path
   * StoragePathService.extractPath('user-123/doc.pdf')
   * // Result: 'user-123/doc.pdf'
   * ```
   */
  static extractPath(
    fullPath: string | null | undefined,
    expectedBucket?: StorageBucket,
  ): Result<string> {
    if (!fullPath) {
      return Result.fail(ServiceError.validation('Storage path is required'));
    }

    const trimmed = fullPath.trim();
    if (trimmed === '') {
      return Result.fail(
        ServiceError.validation('Storage path cannot be empty'),
      );
    }

    // Handle URLs (signed or public)
    if (trimmed.startsWith('https://') || trimmed.startsWith('http://')) {
      return this.extractFromUrl(trimmed, expectedBucket);
    }

    // Handle paths with bucket prefix
    if (expectedBucket && trimmed.startsWith(`${expectedBucket}/`)) {
      return Result.ok(trimmed.slice(expectedBucket.length + 1));
    }

    // Check if path starts with any known bucket
    for (const bucket of Object.values(StorageBuckets)) {
      if (trimmed.startsWith(`${bucket}/`)) {
        return Result.ok(trimmed.slice(bucket.length + 1));
      }
    }

    // Assume raw storage path
    return Result.ok(trimmed);
  }

  /**
   * Extract storage path from a signed or public URL
   */
  private static extractFromUrl(
    url: string,
    expectedBucket?: StorageBucket,
  ): Result<string> {
    // Signed URL pattern: /storage/v1/object/sign/bucket/path?token=...
    const signedMatch = url.match(/\/storage\/v1\/object\/sign\/([^?]+)/);
    if (signedMatch?.[1]) {
      return this.extractPathWithBucket(signedMatch[1], expectedBucket);
    }

    // Public URL pattern: /storage/v1/object/public/bucket/path
    const publicMatch = url.match(/\/storage\/v1\/object\/public\/([^?]+)/);
    if (publicMatch?.[1]) {
      return this.extractPathWithBucket(publicMatch[1], expectedBucket);
    }

    // Authenticated URL pattern: /storage/v1/object/authenticated/bucket/path
    const authMatch = url.match(
      /\/storage\/v1\/object\/authenticated\/([^?]+)/,
    );
    if (authMatch?.[1]) {
      return this.extractPathWithBucket(authMatch[1], expectedBucket);
    }

    return Result.fail(
      ServiceError.validation(
        'Invalid storage URL format. Expected Supabase storage URL.',
      ),
    );
  }

  /**
   * Extract path from a URL path segment that includes bucket name
   */
  private static extractPathWithBucket(
    pathWithBucket: string,
    expectedBucket?: StorageBucket,
  ): Result<string> {
    const decoded = decodeURIComponent(pathWithBucket);

    // If expecting a specific bucket, strip it
    if (expectedBucket && decoded.startsWith(`${expectedBucket}/`)) {
      return Result.ok(decoded.slice(expectedBucket.length + 1));
    }

    // Check all known buckets
    for (const bucket of Object.values(StorageBuckets)) {
      if (decoded.startsWith(`${bucket}/`)) {
        return Result.ok(decoded.slice(bucket.length + 1));
      }
    }

    // Return as-is if no bucket prefix found (might be raw path in URL)
    return Result.ok(decoded);
  }

  /**
   * Validate storage path format
   *
   * @param storagePath - The path to validate
   * @returns Result containing the validated path or an error
   */
  static validate(storagePath: string): Result<string> {
    if (!storagePath || storagePath.trim() === '') {
      return Result.fail(ServiceError.validation('Storage path is required'));
    }

    const trimmed = storagePath.trim();

    // Check for path traversal
    if (trimmed.includes('..')) {
      return Result.fail(ServiceError.validation('Path traversal not allowed'));
    }

    // Check for absolute paths
    if (trimmed.startsWith('/')) {
      return Result.fail(
        ServiceError.validation(
          'Absolute paths not allowed. Use relative paths.',
        ),
      );
    }

    return Result.ok(trimmed);
  }

  /**
   * Build full storage path with bucket prefix
   *
   * @param bucket - The storage bucket
   * @param storagePath - The path within the bucket
   * @returns Full path in format "bucket/path"
   */
  static buildFullPath(bucket: StorageBucket, storagePath: string): string {
    return `${bucket}/${storagePath}`;
  }

  /**
   * Detect bucket from a full path or URL
   *
   * @param fullPath - The path or URL to analyze
   * @returns The detected bucket or null if not found
   */
  static detectBucket(fullPath: string): StorageBucket | null {
    if (!fullPath) return null;

    // Check URL patterns
    const urlMatch = fullPath.match(
      /\/storage\/v1\/object\/(?:sign|public|authenticated)\/([^/?]+)/,
    );
    if (urlMatch?.[1]) {
      const bucketName = decodeURIComponent(urlMatch[1]);
      return this.findBucketByName(bucketName);
    }

    // Check path prefix
    for (const bucket of Object.values(StorageBuckets)) {
      if (
        fullPath.startsWith(`${bucket}/`) ||
        fullPath.includes(`/${bucket}/`)
      ) {
        return bucket;
      }
    }

    return null;
  }

  /**
   * Find bucket enum value by bucket name string
   */
  private static findBucketByName(name: string): StorageBucket | null {
    for (const bucket of Object.values(StorageBuckets)) {
      if (bucket === name) {
        return bucket;
      }
    }
    return null;
  }

  /**
   * Check if a path looks like a full URL (not just a storage path)
   */
  static isUrl(path: string): boolean {
    return path.startsWith('https://') || path.startsWith('http://');
  }

  /**
   * Check if a path includes a bucket prefix
   */
  static hasBucketPrefix(path: string): boolean {
    if (this.isUrl(path)) return true;

    for (const bucket of Object.values(StorageBuckets)) {
      if (path.startsWith(`${bucket}/`)) {
        return true;
      }
    }
    return false;
  }
}

/**
 * Convenience function for extracting storage path
 *
 * This is a simpler alternative to StoragePathService.extractPath() that returns
 * null on error instead of a Result type.
 *
 * @param fullPath - The path or URL to extract from
 * @param bucket - Optional bucket to strip from the path
 * @returns The extracted storage path or null on error
 *
 * @example
 * ```typescript
 * import { extractStoragePath, StorageBuckets } from '@kit/shared/storage';
 *
 * const path = extractStoragePath(signedUrl, StorageBuckets.IDENTITY_DOCUMENTS);
 * if (path) {
 *   // Use the path
 * }
 * ```
 */
export function extractStoragePath(
  fullPath: string | null | undefined,
  bucket?: StorageBucket,
): string | null {
  const result = StoragePathService.extractPath(fullPath, bucket);
  return result.isSuccess ? result.getValue() : null;
}

/**
 * Thumbnail transform options for Supabase Image Transforms
 */
export interface ThumbnailTransformOptions {
  width?: number;
  height?: number;
  quality?: number;
  resize?: 'cover' | 'contain' | 'fill';
}

/**
 * Generate a thumbnail URL from a public Supabase storage URL
 *
 * This works by converting the URL from `/object/public/` to `/render/image/public/`
 * and adding transform query parameters.
 *
 * @param publicUrl - The public Supabase storage URL
 * @param options - Transform options (width, height, quality, resize)
 * @returns The transformed URL or the original if not a valid Supabase URL
 *
 * @example
 * ```typescript
 * import { generatePublicThumbnailUrl } from '@kit/shared/storage';
 *
 * const thumbnailUrl = generatePublicThumbnailUrl(publicUrl, { width: 200, height: 200 });
 * ```
 */
export function generatePublicThumbnailUrl(
  publicUrl: string | null | undefined,
  options: ThumbnailTransformOptions = { width: 200, height: 200, quality: 80 },
): string | null {
  if (!publicUrl) return null;

  // Check if it's a Supabase public URL
  const publicMatch = publicUrl.match(
    /^(https?:\/\/[^/]+)\/storage\/v1\/object\/public\/(.+)$/,
  );

  if (!publicMatch) {
    // Not a Supabase public URL, return as-is
    return publicUrl;
  }

  const [, baseUrl, path] = publicMatch;

  // Build transform query parameters
  const params = new URLSearchParams();
  if (options.width) params.set('width', options.width.toString());
  if (options.height) params.set('height', options.height.toString());
  if (options.quality) params.set('quality', options.quality.toString());
  if (options.resize) params.set('resize', options.resize);

  // Convert to render URL format
  return `${baseUrl}/storage/v1/render/image/public/${path}?${params.toString()}`;
}
